import { useOperatorsMileageRevenueSummaryQuery } from '@/api/queries/operators';
import { Grid } from '@mui/material';
import { Box } from '@mui/system';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { Bus2Icon, DistanceUnit, formatMoney, MileageIcon } from 'remote/global';
import { useOperatorDistanceUnit } from '@/api/queries/users';
import { convertDistanceUnit } from 'utils/helpers';

function toFixedWithoutRounding(num: number, decimalPlaces = 0) {
  const factor = 10 ** decimalPlaces;
  return Math.floor(num * factor) / factor;
}

function DashboardAnalyticsView() {
  const { data } = useOperatorsMileageRevenueSummaryQuery();
  const { t, i18n } = useTranslation();
  const distanceUnit = useOperatorDistanceUnit();

  if (!data) return null;

  const currMonth = dayjs().format("MMM'YY");
  const prevMonth = dayjs().subtract(1, 'month').format("MMM'YY");

  return (
    <Grid container sx={{ width: '100%', maxWidth: 1216 }} gap='1rem'>
      <Grid item xs boxShadow='0px 4px 15px rgba(0, 0, 0, 0.08)' sx={{ maxWidth: '400px' }}>
        <Box sx={{ background: 'rgba(239, 246, 255, 0.5)', p: '1rem' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '1rem',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '0.5rem',
              }}
            >
              <MileageIcon />
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'baseline',
                  gap: '0.25rem',
                  fontSize: '14px',
                }}
              >
                <Box sx={{ color: '#2563EB' }}>{t('DashboardView.analytics.mileage')}</Box>
                <Box sx={{ color: '#98A2B3' }}>{currMonth}</Box>
              </Box>
            </Box>
            <Box sx={{ color: '#2563EB', fontWeight: 500 }}>
              {convertDistanceUnit(
                data.currentMonthPlannedKm,
                DistanceUnit.KM,
                distanceUnit
              ).toFixed(0)}{' '}
              {distanceUnit.toLowerCase()}
            </Box>
          </Box>
        </Box>
        <Box sx={{ padding: '1rem' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '1rem',
            }}
          >
            <Box sx={{ color: '#1F2232', fontSize: '14px', fontWeight: 500 }}>
              {t('DashboardView.analytics.completed')}
            </Box>
            <Box sx={{ color: '#C68ABB', fontSize: '24px', lineHeight: '24px', fontWeight: 600 }}>
              {convertDistanceUnit(
                data.currentMonthCompletedKm,
                DistanceUnit.KM,
                distanceUnit
              ).toFixed(0)}{' '}
              {distanceUnit.toLowerCase()}
            </Box>
          </Box>
          <Box sx={{ height: '1px', width: '100%', background: '#E4E7EC', margin: '0.75rem 0' }} />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '1rem',
            }}
          >
            <Box sx={{ fontSize: '14px', color: '#667085' }}>
              {`${t('DashboardView.analytics.completedMileageCost')} ${
                data.kmPriceExclVat != null
                  ? `(${formatMoney(data.kmPriceExclVat, data.currency, i18n.language)})`
                  : ''
              }`}
            </Box>
            <Box sx={{ fontSize: '14px', color: '#1D2939' }}>
              {formatMoney(
                data.currentMonthCompletedPriceExclVat ?? 0,
                data.currency,
                i18n.language
              )}
            </Box>
          </Box>
          <Box sx={{ height: '1px', width: '100%', background: '#E4E7EC', margin: '0.75rem 0' }} />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '1rem',
            }}
          >
            <Box sx={{ fontSize: '14px', color: '#667085' }}>
              {t('DashboardView.analytics.previousMonth')} {`(${prevMonth})`}
            </Box>
            <Box sx={{ fontSize: '14px', color: '#1D2939' }}>
              {convertDistanceUnit(
                data.previousMonthCompletedKm,
                DistanceUnit.KM,
                distanceUnit
              ).toFixed(0)}{' '}
              {distanceUnit.toLowerCase()}
            </Box>
          </Box>
        </Box>
      </Grid>

      <Grid item xs boxShadow='0px 4px 15px rgba(0, 0, 0, 0.08)' sx={{ maxWidth: '400px' }}>
        <Box sx={{ background: 'rgba(239, 246, 255, 0.5)', p: '1rem' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '1rem',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '0.5rem',
              }}
            >
              <Bus2Icon />
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'baseline',
                  gap: '0.25rem',
                  fontSize: '14px',
                }}
              >
                <Box sx={{ color: '#2563EB' }}>{t('DashboardView.analytics.orders')}</Box>
                <Box sx={{ color: '#98A2B3' }}>{currMonth}</Box>
              </Box>
            </Box>
            <Box sx={{ color: '#2563EB', fontWeight: 500 }}>
              {data.currentMonthPlannedOrderCount}
            </Box>
          </Box>
        </Box>
        <Box sx={{ padding: '1rem' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '1rem',
            }}
          >
            <Box sx={{ color: '#1F2232', fontSize: '14px', fontWeight: 500 }}>
              {t('DashboardView.analytics.completed')}
            </Box>
            <Box sx={{ color: '#C68ABB', fontSize: '24px', lineHeight: '24px', fontWeight: 600 }}>
              {data.currentMonthCompletedOrderCount}
            </Box>
          </Box>
          <Box sx={{ height: '1px', width: '100%', background: '#E4E7EC', margin: '0.75rem 0' }} />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '1rem',
            }}
          >
            <Box sx={{ fontSize: '14px', color: '#667085' }}>
              {t('DashboardView.analytics.upcomingOrders')}
            </Box>
            <Box sx={{ fontSize: '14px', color: '#1D2939' }}>
              {data.currentMonthPlannedOrderCount - data.currentMonthCompletedOrderCount}
            </Box>
          </Box>
          <Box sx={{ height: '1px', width: '100%', background: '#E4E7EC', margin: '0.75rem 0' }} />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '1rem',
            }}
          >
            <Box sx={{ fontSize: '14px', color: '#667085' }}>
              {t('DashboardView.analytics.previousMonth')} {`(${prevMonth.toLocaleLowerCase()})`}
            </Box>
            <Box sx={{ fontSize: '14px', color: '#1D2939' }}>
              {data.previousMonthCompletedOrderCount}
            </Box>
          </Box>
        </Box>
      </Grid>

      <Grid item xs boxShadow='0px 4px 15px rgba(0, 0, 0, 0.08)' sx={{ maxWidth: '400px' }}>
        <Box sx={{ background: 'rgba(239, 246, 255, 0.5)', p: '1rem' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '1rem',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '0.5rem',
              }}
            >
              <Bus2Icon />
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'baseline',
                  gap: '0.25rem',
                  fontSize: '14px',
                }}
              >
                <Box sx={{ color: '#2563EB' }}>{t('DashboardView.analytics.revenue')}</Box>
                <Box sx={{ color: '#98A2B3' }}>{currMonth}</Box>
              </Box>
            </Box>
            <Box sx={{ color: '#2563EB', fontWeight: 500 }}>
              {formatMoney(
                toFixedWithoutRounding(data.currentMonthPlannedRevenue),
                data.currency,
                i18n.language
              )}
            </Box>
          </Box>
        </Box>
        <Box sx={{ padding: '1rem' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '1rem',
            }}
          >
            <Box sx={{ color: '#1F2232', fontSize: '14px', fontWeight: 500 }}>
              {t('DashboardView.analytics.ofCompletedOrders')}
            </Box>
            <Box sx={{ color: '#C68ABB', fontSize: '24px', lineHeight: '24px', fontWeight: 600 }}>
              {formatMoney(
                toFixedWithoutRounding(data.currentMonthCompletedRevenue),
                data.currency,
                i18n.language
              )}
            </Box>
          </Box>
          <Box sx={{ height: '1px', width: '100%', background: '#E4E7EC', margin: '0.75rem 0' }} />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '1rem',
            }}
          >
            <Box sx={{ fontSize: '14px', color: '#667085' }}>
              {t('DashboardView.analytics.ofUpcomingOrders')}
            </Box>
            <Box sx={{ fontSize: '14px', color: '#1D2939' }}>
              {formatMoney(
                toFixedWithoutRounding(
                  data.currentMonthPlannedRevenue - data.currentMonthCompletedRevenue
                ),
                data.currency,
                i18n.language
              )}
            </Box>
          </Box>
          <Box sx={{ height: '1px', width: '100%', background: '#E4E7EC', margin: '0.75rem 0' }} />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '1rem',
            }}
          >
            <Box sx={{ fontSize: '14px', color: '#667085' }}>
              {t('DashboardView.analytics.ofPreviousMonthOrders')} {`(${prevMonth})`}
            </Box>
            <Box sx={{ fontSize: '14px', color: '#1D2939' }}>
              {formatMoney(
                toFixedWithoutRounding(data.previousMonthCompletedRevenue),
                data.currency,
                i18n.language
              )}
            </Box>
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
}

export default DashboardAnalyticsView;
